// Tour Form Page - Shared component for creating and editing tours
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
  CircularProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';

import { useTours } from '../../tour/hooks/useTours';
import { Tour, WaypointListItem } from '../../tour/types';
import WaypointConfiguration from '../components/WaypointConfiguration';
import { useAppSelector } from '../../../store/hooks';
import { selectSelectedMuseum } from '../../../store/slices/museumSlice';
import { tourService } from '../services/tourService';
import { contentService } from '../../content/services/contentService';

const steps = ['Basic Information', 'Waypoint Configuration', 'Review Changes'];

interface TourFormData {
  tour_id: number;
  description: string;
  duration: number;
  museum_level: string;
  tour_type: 'short' | 'long';
  is_active: boolean;
}

const TourFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  // Get current user from Redux store
  const { user } = useAppSelector(state => state.auth);
  const isAdmin = user?.role === 'admin';
  const canEdit = isAdmin; // Only Admin can edit, Super User has same permissions as User

  // Redirect non-admin users
  useEffect(() => {
    if (!canEdit) {
      navigate('/tours');
    }
  }, [canEdit, navigate]);

  // Get selected museum from Redux store
  const selectedMuseum = useAppSelector(selectSelectedMuseum);

  // Debug: Log museum selection changes
  useEffect(() => {
    console.log('TourFormPage selectedMuseum updated:', selectedMuseum);
  }, [selectedMuseum]);

  const [activeStep, setActiveStep] = useState(0);
  const [tour, setTour] = useState<Tour | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [availableLevels, setAvailableLevels] = useState<string[]>(['1']);
  const [hasAnyContent, setHasAnyContent] = useState<boolean>(true);
  const [noContentDialogOpen, setNoContentDialogOpen] = useState(false);

  // Form data
  const [formData, setFormData] = useState<TourFormData>({
    tour_id: 1,
    description: '',
    duration: 30,
    museum_level: '1',
    tour_type: 'long',
    is_active: true,
  });

  // Waypoints
  const [waypoints, setWaypoints] = useState<WaypointListItem[]>([]);

  // Snackbar
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [tourIdFieldError, setTourIdFieldError] = useState<string | null>(null);

  const { data: tours } = useTours();

  // Auto-increment function for tour IDs
  const getNextAvailableTourId = async (museumCode: string): Promise<number> => {
    try {
      // Prefer server-calculated next ID to avoid races
      return await tourService.getNextTourId(museumCode);
    } catch (error) {
      console.error('Error getting next tour ID from server, falling back:', error);
      try {
        const existingTours = await tourService.getToursByMuseum(museumCode);
        const existingIds = existingTours.map(tour => tour.tour_id);
        let nextId = 1;
        while (existingIds.includes(nextId)) nextId++;
        return nextId;
      } catch (e) {
        console.error('Fallback next ID calculation failed:', e);
        return 1;
      }
    }
  };

  // Load museum details to get occupied levels when museum is selected
  useEffect(() => {
    if (selectedMuseum?.id) {
      // Fetch museum details to get occupied levels
      fetch(`/api/museums/${selectedMuseum.id}/`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      })
        .then(res => res.json())
        .then(data => {
          if (data.number_of_levels && Array.isArray(data.number_of_levels)) {
            setAvailableLevels(data.number_of_levels);
            // Set default level to first available if current is not in list
            setFormData(prev => {
              if (!data.number_of_levels.includes(prev.museum_level)) {
                return { ...prev, museum_level: data.number_of_levels[0] || '1' };
              }
              return prev;
            });
          }
        })
        .catch(err => console.error('Error fetching museum details:', err));
    }
  }, [selectedMuseum]);

  // Check if there is at least one content for the selected museum
  useEffect(() => {
    const checkContents = async () => {
      if (!selectedMuseum?.museum_code) {
        setHasAnyContent(true); // do not block if museum not selected yet
        return;
      }
      try {
        const contents = await contentService.getContents({ museum_code: selectedMuseum.museum_code });
        setHasAnyContent(Array.isArray(contents) ? contents.length > 0 : true);
      } catch (e) {
        // In case of error, do not block the user but log it
        console.error('Failed to check contents for museum:', e);
        setHasAnyContent(true);
      } finally {
      }
    };

    checkContents();
  }, [selectedMuseum]);

  useEffect(() => {
    if (isEditMode && tours && id) {
      const foundTour = tours.find(t => t.id === parseInt(id));
      if (foundTour) {
        setTour(foundTour);
        setFormData({
          tour_id: foundTour.tour_id,
          description: foundTour.description || '',
          duration: foundTour.duration || 30, // Ensure duration is never null
          museum_level: foundTour.museum_level || '1',
          tour_type: foundTour.tour_type || 'long',
          is_active: foundTour.is_active,
        });
        // Add stable IDs to waypoints for drag-and-drop
        const waypointsWithIds = (foundTour.waypoint_lists || []).map((wp, index) => ({
          ...wp,
          id: wp.id || `${wp.waypoint_name}-${index}-${Date.now()}`,
        }));
        setWaypoints(waypointsWithIds);
      } else {
        setError('Tour not found');
      }
      setLoading(false);
    } else if (!isEditMode && selectedMuseum?.museum_code) {
      // Create mode - auto-generate tour ID for new tours
      getNextAvailableTourId(selectedMuseum.museum_code)
        .then(nextId => {
          setFormData(prev => ({ ...prev, tour_id: nextId }));
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } else if (!isEditMode) {
      // Create mode but no museum selected yet
      setLoading(false);
    }
  }, [tours, id, isEditMode, selectedMuseum]);

  // Recompute next ID whenever selected museum changes in create mode
  useEffect(() => {
    if (!isEditMode && selectedMuseum?.museum_code) {
      getNextAvailableTourId(selectedMuseum.museum_code).then(nextId =>
        setFormData(prev => ({ ...prev, tour_id: nextId })),
      );
    }
  }, [isEditMode, selectedMuseum?.museum_code]);

  const handleInputChange = (field: keyof TourFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]:
        field === 'duration' || field === 'tour_id'
          ? parseInt(value as string) || (field === 'duration' ? 30 : 1)
          : value,
    }));
  };

  const handleNext = () => {
    // When moving to waypoint configuration, ensure content exists first
    if (activeStep === 0 && !hasAnyContent) {
      setNoContentDialogOpen(true);
      return;
    }
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleCancel = () => {
    navigate('/tours');
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        return formData.tour_id > 0;
      case 1:
        return waypoints.length > 0;
      case 2:
        return true;
      default:
        return false;
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setTourIdFieldError(null);

      // In edit mode, use tour's museum; in create mode, require selectedMuseum
      const museumId = isEditMode ? tour?.museum : selectedMuseum?.id;

      console.log('TourFormPage handleSave:', {
        isEditMode,
        tourMuseum: tour?.museum,
        selectedMuseum,
        museumId,
        selectedMuseumId: selectedMuseum?.id,
      });

      if (!museumId) {
        console.error('Museum selection issue:', { isEditMode, tour: tour?.museum, selectedMuseum });
        setSnackbarMessage('Please select a museum before creating a tour.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setSaving(false);
        return;
      }
      const tourData = {
        tour_id: formData.tour_id,
        description: formData.description,
        duration: formData.duration || 30, // Ensure duration is never null, default to 30
        museum: museumId, // Use the museum ID we validated above
        museum_level: formData.museum_level,
        tour_type: formData.tour_type,
        waypoint_lists: waypoints.map(wp => ({
          name: wp.name ?? wp.content_name ?? '',
          destination: wp.destination ?? wp.waypoint_name ?? '',
          version: wp.version,
          floor: wp.floor ?? 1,
          is_halfway: wp.is_halfway ?? false,
          is_return_point: wp.is_return_point ?? false,
        })),
        is_active: formData.is_active,
      };

      console.log(isEditMode ? 'Updating tour:' : 'Creating tour:', tourData);

      // Import tourService dynamically to avoid circular imports
      const { tourService } = await import('../../tour/services/tourService');

      if (isEditMode && tour) {
        await tourService.updateTour(tour.id, tourData);
        setSnackbarMessage('Tour updated successfully!');
      } else {
        await tourService.createTour(tourData);
        setSnackbarMessage('Tour created successfully!');
      }

      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      navigate('/tours');
    } catch (error: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} tour:`, error);
      // Map backend field errors if available
      const data = error?.response?.data;
      if (data) {
        if (data.tour_id && Array.isArray(data.tour_id)) {
          setTourIdFieldError(String(data.tour_id[0]));
          setSnackbarMessage(String(data.tour_id[0]));
          // Try to recompute next available ID for the museum
          if (selectedMuseum?.museum_code) {
            getNextAvailableTourId(selectedMuseum.museum_code).then(nextId =>
              setFormData(prev => ({ ...prev, tour_id: nextId })),
            );
          }
        } else if (typeof data.detail === 'string') {
          setSnackbarMessage(data.detail);
        } else if (typeof data === 'string') {
          setSnackbarMessage(data);
        } else {
          setSnackbarMessage('Validation failed. Please check the form fields.');
        }
      } else {
        setSnackbarMessage(error.message || `Failed to ${isEditMode ? 'update' : 'create'} tour`);
      }
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const handleWaypointsChange = (newWaypoints: WaypointListItem[]) => {
    setWaypoints(newWaypoints);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || (isEditMode && !tour)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Tour not found'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/tours')}>
          Back to Tours
        </Button>
      </Box>
    );
  }

  const renderBasicInfo = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Basic Information
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Tour ID"
            type="number"
            value={formData.tour_id}
            fullWidth
            required
            disabled
            error={formData.tour_id <= 0 || Boolean(tourIdFieldError)}
            helperText={
              tourIdFieldError
                ? tourIdFieldError
                : formData.tour_id <= 0
                  ? 'Tour ID must be greater than 0'
                  : 'Tour ID is generated automatically for each museum.'
            }
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={handleInputChange('description')}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            label="Duration (minutes)"
            type="number"
            value={formData.duration || 30}
            onChange={handleInputChange('duration')}
            fullWidth
            required
            inputProps={{ min: 1 }}
          />
          <FormControl fullWidth>
            <InputLabel>Museum Level</InputLabel>
            <Select
              value={formData.museum_level}
              label="Museum Level"
              onChange={e => setFormData({ ...formData, museum_level: e.target.value })}
            >
              {availableLevels.map(level => (
                <MenuItem key={level} value={level}>
                  {level === 'B' ? 'Basement' : level}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Tour Type</InputLabel>
            <Select
              value={formData.tour_type}
              label="Tour Type"
              onChange={e => setFormData({ ...formData, tour_type: e.target.value as 'short' | 'long' })}
            >
              <MenuItem value="long">Long (full route → long_waypoint.yaml)</MenuItem>
              <MenuItem value="short">Short (abbreviated route → short_waypoint.yaml)</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </CardContent>
    </Card>
  );

  const renderWaypointConfig = () => (
    <WaypointConfiguration
      waypoints={waypoints}
      onWaypointsChange={handleWaypointsChange}
      museumCode={isEditMode ? tour?.museum_code || 'ACM' : selectedMuseum?.museum_code || 'ACM'}
      museumName={selectedMuseum?.name}
    />
  );

  const renderReview = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tour Details
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Tour ID
            </Typography>
            <Typography variant="body1">{formData.tour_id}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Description
            </Typography>
            <Typography variant="body1">{formData.description || 'No description'}</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Duration
            </Typography>
            <Typography variant="body1">{formData.duration} minutes</Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Museum Level
            </Typography>
            <Typography variant="body1">
              {formData.museum_level === 'B' ? 'Basement' : formData.museum_level}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Tour Type
            </Typography>
            <Typography variant="body1">
              {formData.tour_type === 'short' ? 'Short (short_waypoint.yaml)' : 'Long (long_waypoint.yaml)'}
            </Typography>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Waypoints
            </Typography>
            <Typography variant="body1">{waypoints.length} waypoints</Typography>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Tour Route
          </Typography>
          {waypoints.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No waypoints configured
            </Typography>
          ) : (
            <List>
              {waypoints.map((waypoint, index) => (
                <ListItem key={waypoint.id}>
                  <ListItemText
                    primary={`${index + 1}. ${waypoint.name ?? waypoint.content_name ?? ''}`}
                    secondary={`Destination: ${waypoint.destination ?? waypoint.waypoint_name ?? ''} | v${waypoint.version} | Floor ${waypoint.floor ?? 1}${waypoint.is_halfway ? ' · halfway' : ''}${waypoint.is_return_point ? ' · return' : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={handleCancel}>
            Back
          </Button>
          <Typography variant="h4">{isEditMode ? `Edit Tour: ${tour?.tour_id}` : 'Create New Tour'}</Typography>
        </Box>
      </Box>

      {/* Museum Selection Alert */}
      {!selectedMuseum && !isEditMode && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please select a museum from the navigation sidebar before creating a tour.
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step Content */}
      <Box sx={{ mb: 3 }}>
        {activeStep === 0 && renderBasicInfo()}
        {activeStep === 1 && renderWaypointConfig()}
        {activeStep === 2 && renderReview()}
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={handleBack} disabled={activeStep === 0}>
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button onClick={handleCancel}>Cancel</Button>
          {activeStep === steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleSave}
              startIcon={<SaveIcon />}
              disabled={!isStepValid(activeStep) || saving}
            >
              {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Create Tour'}
            </Button>
          ) : (
            <Button variant="contained" onClick={handleNext} disabled={!isStepValid(activeStep)}>
              Next
            </Button>
          )}
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* No Content Dialog */}
      <Dialog open={noContentDialogOpen} onClose={() => setNoContentDialogOpen(false)}>
        <DialogTitle>Content required before adding waypoints</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You need to create at least one piece of content for this museum before configuring waypoints. Each waypoint
            references content by waypoint name and version.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoContentDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setNoContentDialogOpen(false);
              navigate('/content/create');
            }}
          >
            Go to Create Content
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TourFormPage;
